import { apiService } from './apiService';

export interface DXBlueprintContent {
  id: string;
  SK: string;
  dx_number: string;
  slug: string;
  title_th: string;
  title_en?: string;
  short_description_th: string;
  short_description_en: string;
  problem_statement_th?: string;
  problem_statement_en?: string;
  pain_point_items?: Array<{
    title_th: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
  }>;
  gap_to_improve_items?: Array<{
    title_th: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
  }>;
  strategic_objectives_th?: string;
  strategic_objectives_en?: string;
  business_impacts_th?: string;
  business_impacts_en?: string;
  end_goals_th?: string;
  end_goals_en?: string;
  key_capabilities_pillars_th?: string;
  key_capabilities_pillars_en?: string;
  use_case_items?: Array<{
    name_th: string;
    name_en?: string;
    description_th?: string;
    description_en?: string;
    use_case_details?: Array<{
      current_step_stones_th: string;
      current_step_stones_en?: string;
      next_move_th?: string;
      next_move_en?: string;
      potential_value_creations_th?: string;
      potential_value_creations_en?: string;
    }>;
  }>;
  technologies_enablers_items?: Array<{
    title_th: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
  }>;
  technologies_enablers_images?: string[]; // Now stores asset IDs instead of URLs
  key_challenges_items?: Array<{
    title_th?: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
  }>;
  timeline_items?: Array<{
    phase_th: string;
    phase_en?: string;
    timeline_th?: string;
    timeline_en?: string;
    status?: 'in_progress' | 'published';
  }>;
  content_owner?: string;
  content_members?: string[];
  status: 'draft' | 'published' | 'archived' | 'deleted';
  edit_status?: 'draft' | 'published';
  category?: string;
  type?: string;
  author_id?: string;
  author_name?: string;
  published_at?: string;
  published_version?: string;
  version_number?: number;
  publish_timestamp?: string;
  archived_at?: string | null;
  views?: number;
  likes?: number;
  shares?: number;
  comments_count?: number;
  version?: number;
  parent_id?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface ContentListResponse {
  contents: DXBlueprintContent[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
    next_page?: number;
    prev_page?: number;
  };
}

export interface ContentListParams {
  page?: number;
  limit?: number;
  category?: string;
  status?: 'draft' | 'published' | 'archived' | 'deleted';
  sort_by?: 'created_at' | 'updated_at' | 'published_at' | 'title_th' | 'title_en' | 'dx_number' | 'views' | 'likes';
  order?: 'asc' | 'desc';
  search?: string;
}

export interface ContentVersion {
  version: string;
  version_number: number;
  publisher_id: string;
  publisher_name: string;
  publish_timestamp: string;
  dx_number: string;
  title_th: string;
  title_en: string;
}

export interface ContentVersionListResponse {
  content_sk: string;
  total_versions: number;
  versions: ContentVersion[];
}

export interface ContentCreateRequest {
  dx_number: string;
  title_th?: string;
  title_en?: string;
  short_description_th?: string;
  short_description_en?: string;
  problem_statement_th?: string;
  problem_statement_en?: string;
  pain_point_items?: Array<{
    title_th: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
  }>;
  gap_to_improve_items?: Array<{
    title_th: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
  }>;
  strategic_objectives_th?: string;
  strategic_objectives_en?: string;
  business_impacts_th?: string;
  business_impacts_en?: string;
  end_goals_th?: string;
  end_goals_en?: string;
  key_capabilities_pillars_th?: string;
  key_capabilities_pillars_en?: string;
  use_case_items?: Array<{
    name_th?: string;
    name_en?: string;
    description_th?: string;
    description_en?: string;
    use_case_details?: Array<{
      current_step_stones_th?: string;
      current_step_stones_en?: string;
      next_move_th?: string;
      next_move_en?: string;
      potential_value_creations_th?: string;
      potential_value_creations_en?: string;
    }>;
  }>;
  technologies_enablers_items?: Array<{
    title_th?: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
    image?: string;
  }>;
  technologies_enablers_images?: string[]; // Asset IDs for technology images
  timeline_items?: Array<{
    phase_th?: string;
    phase_en?: string;
    timeline_th?: string;
    timeline_en?: string;
    status?: string;
  }>;
  key_challenges_items?: Array<{
    title_th?: string;
    title_en?: string;
    description_th?: string;
    description_en?: string;
  }>;
  content_owner?: string;
  content_members?: string[];
  status?: 'draft' | 'published';
  metadata?: Record<string, any>;
}

class ContentService {
  /**
   * Get list of contents with optional filters
   */
  async getContents(params?: ContentListParams): Promise<ContentListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const endpoint = `/api/contents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiService.get<ContentListResponse>(endpoint);
  }

  /**
   * Get list of public contents (no token required, published only)
   */
  async getPublicContents(params?: ContentListParams): Promise<ContentListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.your-backend.com'}/contents`);
    return await response.json();
  }

  /**
   * Get a specific content by SK
   */
  async getContentBySK(SK: string): Promise<DXBlueprintContent> {
    return await apiService.get<DXBlueprintContent>(`/api/contents/${SK}`);
  }

  /**
   * Create new content
   */
  async createContent(content: ContentCreateRequest): Promise<{ message: string; content: DXBlueprintContent }> {
    return await apiService.post<{ message: string; content: DXBlueprintContent }>('/api/contents', content);
  }

  /**
   * Update existing content by SK
   */
  async updateContent(SK: string, content: Partial<ContentCreateRequest>): Promise<{ message: string; content: DXBlueprintContent }> {
    return await apiService.put<{ message: string; content: DXBlueprintContent }>(`/api/contents/${SK}`, content);
  }

  /**
   * Delete content by SK
   */
  async deleteContent(SK: string): Promise<{ message: string }> {
    return await apiService.delete<{ message: string }>(`/api/contents/${SK}`);
  }

  async publishContent(SK: string): Promise<{ message: string; }> {
    return await apiService.post<{ message: string }>(`/api/contents/${SK}/publish`);
  }

  async unpublishContent(SK: string): Promise<{ message: string; }> {
    return await apiService.post<{ message: string }>(`/api/contents/${SK}/unpublish`);
  }

  async republishContent(SK: string, version: string): Promise<{ message: string; content: DXBlueprintContent }> {
    return await apiService.post<{ message: string; content: DXBlueprintContent }>(`/api/contents/${SK}/republish`, { version });
  }

  /**
   * Search contents
   */
  async searchContents(searchParams: {
    query?: string;
    filters?: {
      status?: string[];
      category?: string[];
      dx_numbers?: string[];
      author_ids?: string[];
      tags?: string[];
      date_range?: {
        field: string;
        start?: string;
        end?: string;
      };
    };
    sort?: {
      field: string;
      order: 'asc' | 'desc';
    };
    pagination?: {
      page: number;
      limit: number;
    };
  }): Promise<ContentListResponse> {
    return await apiService.post<ContentListResponse>('/api/contents/search', searchParams);
  }

  /**
   * Get content versions by SK
   */
  async getContentVersions(SK: string): Promise<ContentVersionListResponse> {
    return await apiService.get<ContentVersionListResponse>(`/api/contents/${SK}/versions`);
  }

  /**
   * Get a specific version of content by SK and version number
   */
  async getContentByVersion(SK: string, version: string): Promise<DXBlueprintContent> {
    return await apiService.get<DXBlueprintContent>(`/api/contents/${SK}/versions/${version}`);
  }

  /**
   * Create an editable version from a specific version
   */
  async createEditableVersion(SK: string, version: string): Promise<{ message: string; content: DXBlueprintContent }> {
    return await apiService.post<{ message: string; content: DXBlueprintContent }>(`/api/contents/${SK}/versions/${version}/edit`);
  }
}

// Export singleton instance
export const contentService = new ContentService();
export default contentService;